<template>
    <div class="asc-swedbank">
        <div class="asc-swedbank-logo">
            <icon-swedbank />
        </div>
        <div class="asc-swedbank-modal">
            <content-tabs
                :color="sc.COLOR"
                :disabled="step || loading"
                :auth-method="authMethod"
                @changeTab="changeAuthenticationMethod($event)"
                :authMethods="authMethods"
            />
            <div class="asc-swedbank-modal-content" v-if="loading">
                <asc-loader :color="sc.COLOR" />
            </div>
            <div class="asc-swedbank-modal-content" v-else>
                <content-failed v-if="step === sc.FAILED" />

                <content-successful :color="sc.COLOR" v-else-if="step === sc.SUCCESSFUL" />

                <content-information v-else-if="step === sc.SHOW_TEXT"
                                     :code="code"
                                     :show-payment-data="!!(subStep && subStep === sc.ACCEPT_PAYMENT)"
                                     :show-accounts-data="!!(subStep && subStep === sc.ACCEPT_PAYMENT && accounts.length > 0)"
                                     :payment="payment"
                                     :accounts="accounts"
                                     :selected-account="selectedAccount"
                                     :payment-type="paymentType"
                                     :color="sc.COLOR"
                                     :sc="sc"
                                     @setAccount="handleSetSelectedAccountSelect">
                    {{ t(this.text) }}
                </content-information>

                <swedbank-insert-user-info v-else />
            </div>
            <div
                class="asc-swedbank-modal-actions"
                :class="{ 'asc-swedbank-modal-actions__right': !step}"
                v-if="!loading && !code"
            >
                <asc-button @clickBtn="handleProceedClick" class="asc-swedbank-modal-btn"
                    >{{ subStep === sc.ACCEPT_PAYMENT ? t('Pay') : t('Proceed') }}
                </asc-button>
            </div>
        </div>
    </div>
</template>
<script>
import IconSwedbank from "@/components/icons/icon-swedbank.vue";
import AscButton from "@/components/asc-button.vue";
import AscInput from "@/components/asc-input.vue";
import ContentTabs from "@/components/content/content-tabs.vue";
import SwedbankInsertUserInfo from "@/components/swedbank/swedbank-insert-user-info.vue";
import ContentInformation from "@/components/content/content-information.vue";
import ContentSuccessful from "@/components/content/content-successful.vue";
import ContentFailed from "@/components/content/content-failed.vue";
import AscLoader from "@/components/asc-loader.vue";
import { mapActions, mapMutations, mapState } from "vuex";

import { swedbankConstants } from "@/constants";
import {translateMixin} from "@/mixins/translateMixin";

export default {
    name: "swedbank-main",
    components: {
        IconSwedbank,
        AscButton,
        AscInput,
        ContentTabs,
        SwedbankInsertUserInfo,
        ContentInformation,
        ContentSuccessful,
        ContentFailed,
        AscLoader
    },
    props: {
        isBulkPayment: {
            default: false,
            type: Boolean,
        },
        activeBank: {
            default: null,
            type: String,
        },
        clientCountry: {
            default: 'et_EE',
            type: String,
        },
        payment: {
            default: {},
            type: Object
        }
    },
    mixins: [translateMixin],
    data() {
        return {
            timeoutTimer: null,
            sc: swedbankConstants
        };
    },
    async mounted() {
        // Both bulk-payment and regular payment use this component
        this.setPaymentType(this.isBulkPayment ? this.sc.PAYMENT_TYPE_BULK_PAYMENT : this.sc.PAYMENT_TYPE_REDIRECT);

        if (this.clientCountry === "lv_LV") await this.setUser({ prop: "phone", value: "+371" });
        else if (this.clientCountry === "lt_LT") await this.setUser({ prop: "phone", value: "+370" });
        this.addPaymentInfo(this.payment)
    },
    computed: {
        ...mapState("swedbank", [
            "user",
            "step",
            "authMethod",
            "authMethods",
            "code",
            "loading",
            "text",
            "subStep",
            "accounts",
            "selectedAccount",
            "paymentType"
        ]),
    },
    methods: {
        ...mapMutations("swedbank", [
            "setLoading",
            "setAuthMethod",
            "setUser",
            "setStep",
            "resetStates",
            "setSubStep",
            "setText",
            "addPaymentInfo",
            "setPaymentType",
            "setSelectedAccount"

        ]),
        ...mapActions("swedbank", [
            "startSwedbankAuthorization",
            "pingSwedbankAuthorization",
            "startPaymentAuthorization",
            "pingPaymentAuthorization",
        ]),
        ...mapActions("swedbank", ["startBankAuthorizing"]),
        handleUserValueChange(value, prop) {
            this.setUser({ prop: prop, value: value });
        },
        changeAuthenticationMethod(method) {
            this.setAuthMethod(method);
        },
        async handleProceedClick() {
            let result = null;
            if (!this.step) {
                if (this.authMethod === "ID_CARD") {
                    this.setLoading(true);
                    const returnUrlPart = this.isBulkPayment ? 'bulk-payment' : 'payment'

                    window.location = `${process.env.VUE_APP_URL}/app/psd2/${returnUrlPart}/${window.TarkPay.payment_id}/${this.activeBank}?locale=${this.clientCountry}&ignore-embed=1`;

                    return;
                }
                //First time if Swedbank is clicked
                result = await this.startSwedbankAuthorization({ swift: this.activeBank });
            } else if (this.subStep === this.sc.ACCEPT_PAYMENT) {
                // After authorization, to start payment
                result = await this.startPaymentAuthorization({ swift: this.activeBank });
            } else if (this.step === this.sc.SUCCESSFUL) {
                this.$emit("done");
            } else if (this.step === this.sc.FAILED) {
                // If something went wrong
                this.resetStates();
                this.$emit("resetFlow");
            }
            if (result) {
                this.timeoutTimer = setTimeout(() => this.pingForResult(this.subStep), 9000);
            }
        },
        async pingForResult(flow) {
            let response = null;
            if (flow === this.sc.AUTHORIZATION) {
                response = await this.pingSwedbankAuthorization({ swift: this.activeBank });
            } else if (flow === this.sc.CONFIRM_PAYMENT) {
                response = await this.pingPaymentAuthorization({ swift: this.activeBank });
            }

            if (response) {
                clearTimeout(this.timeoutTimer);
                if (flow === this.sc.CONFIRM_PAYMENT) {
                    this.resetStates();
                    this.$emit("done");
                    const returnUrlPart = this.isBulkPayment ? 'return-bulk-payment-transaction' : 'redirect-transaction'

                    window.location = `${process.env.VUE_APP_URL}/app/psd2/${returnUrlPart}/?state=${window.TarkPay.payment_id}`;
                }
            } else {
                if (this.step !== this.sc.FAILED) {
                    this.timeoutTimer = setTimeout(() => this.pingForResult(flow), 2500);
                }
            }
        },
        handleSetSelectedAccountSelect(e) {
            this.setSelectedAccount(e);
        }
    }
};
</script>
<style scoped lang="scss">
@import "../../assets/scss/variables";
@import "../../assets/scss/mixins";

.asc-swedbank {
    display: flex;
    justify-content: center;
    flex-direction: column;
    font-family: "Arial", serif;
    font-style: normal;
    font-weight: 300;
    font-size: 15px;
    line-height: 19px;
    color: $defaultTextColor;

    &-bg {
        background: #fbf2eb;
    }
    &-logo {
        text-align: center;
        margin-bottom: 16px;
    }

    &-modal {
        display: flex;
        flex-direction: column;
        max-width: 500px;
        background: #ffffff;
        min-height: 300px;
        width: 100%;
        padding-bottom: 40px;
        @include border-radius(12px);
        @include box-shadow(0 12px 34px rgba(0, 0, 0, 0.04));

        &-content {
            display: flex;
            align-items: center;
            padding: 40px 40px 24px 40px;
            text-align: center;
            flex-grow: 1;
        }

        &-actions {
            display: flex;
            justify-content: center;
            padding: 0 40px;

            button {
                background: $swedbankColor;
                min-width: 220px;
            }

            &__right {
                justify-content: flex-end;
            }
        }
    }
}

@media only screen and (max-width: $smallMobileMQ) {
    .asc-swedbank {
        &-modal {
            &-actions {
                &__right {
                    justify-content: center;
                }
            }
        }
    }
}
</style>
